import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import NotificationSound from '@assets/notification.mp3';
import { HttpHelper } from '@helpers/httpHelper';
import { UrlManager } from '@providers/urlManager';

const notificationSound = new Audio(NotificationSound);

const useNotificationStore = create(
    persist(
        (set, get) => ({
            notifications: [],

            // Add a notification
            addNotification: notification => {
                const defaultNotification = {
                    type: 'info',
                    title: null,
                    message: null,
                    timeOut: 5000,
                    customClassName: '',
                    onClick: () => {},
                    priority: false,
                    createdAt: new Date(),
                    playSound: true,
                };

                // Play sound if not explicitly disabled
                if (notification.playSound !== false) {
                    notificationSound
                        .play()
                        .catch(err => console.warn('Could not play notification sound:', err));
                }

                set(state => ({
                    notifications: notification.priority
                        ? [{ ...defaultNotification, ...notification }, ...state.notifications]
                        : [...state.notifications, { ...defaultNotification, ...notification }],
                }));

                // Auto remove notification after timeout if specified
                if (notification.timeOut !== 0) {
                    setTimeout(() => {
                        get().removeNotification(defaultNotification.id);
                    }, notification.timeOut || defaultNotification.timeOut);
                }
            },

            // Remove a notification
            removeNotification: id => {
                set(state => ({
                    notifications: state.notifications.filter(n => n.id !== id),
                }));
                HttpHelper.fetch(UrlManager.deleteNotificationUrl(), {
                    method: 'POST',
                    body: { id: id },
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                    },
                });
            },

            // Clear all notifications
            clearNotifications: () => {
                // Get all notification IDs
                const allIds = get()
                    .notifications.map(n => n.id)
                    .join(',');

                // Clear local state
                set({ notifications: [] });

                // Send POST request to clear all notifications
                HttpHelper.fetch(UrlManager.deleteNotificationUrl(), {
                    method: 'POST',
                    body: { id: allIds },
                    headers: {
                        'Content-Type': 'application/x-www-form-urlencoded',
                    },
                });
            },

            // Helper methods for different notification types
            success: (message, title, timeOut) =>
                get().addNotification({ type: 'success', message, title, timeOut }),

            error: (message, title, timeOut) =>
                get().addNotification({ type: 'error', message, title, timeOut }),

            warning: (message, title, timeOut) =>
                get().addNotification({ type: 'warning', message, title, timeOut }),

            info: (message, title, timeOut) =>
                get().addNotification({ type: 'info', message, title, timeOut }),

            primary: (message, title, timeOut) =>
                get().addNotification({ type: 'primary', message, title, timeOut }),

            secondary: (message, title, timeOut) =>
                get().addNotification({ type: 'secondary', message, title, timeOut }),
        }),
        {
            name: 'notifications-storage',
            storage: createJSONStorage(() => localStorage),
        },
    ),
);

export default useNotificationStore;
